'use client';

import { GetExperiencesFromCatgRes } from '@/actions/experiences/types';
import { useTranslation } from '@/contexts/dictionaryContext';
import { useLocale } from '@/hooks/useLocale';
import useLocalStorage from '@/hooks/useLocalStorage';
import { Experience } from '@/services/experienceTypes';
import { normalizeRegion } from '@/utils/normalizeRegion';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { FC, HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { AnimatedLink } from '../AnimatedLink';
import Button from '../Button';
import { CategoryFilter } from './CategoryFilter';
import { ExperienceType, ShortList } from './ExperienceType';

interface ICategorySectionsProps extends HTMLAttributes<HTMLDivElement> {
  categoryTitle: string;
  experiences: GetExperiencesFromCatgRes['experiencesGroupedByCategory'];
}

export const CategorySections: FC<ICategorySectionsProps> = ({
  categoryTitle,
  experiences,
}) => {
  const lang = useLocale();
  const { category: dict } = useTranslation();
  const { region } = useParams() as { region: 'sicily' | 'puglia' };

  const categoryExperiences = useMemo(
    () => Object.entries(experiences?.categories!),
    [experiences],
  );

  const [chosenTypes, setChosenTypes] = useState<string[]>([]);
  const [chosenLanguages, setChosenLanguages] = useState<string[]>([]);
  const [chosenZone, setChosenZone] = useState({
    label: `${dict.filter.zoneOptions[0].label}`,
    value: 'any',
  });
  const [chosenPrice, setChosenPrice] = useState({
    minPrice: 0,
    maxPrice: 3000,
  });
  const [openFilter, setOpenFilter] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [favourites, setFavourites] = useLocalStorage(
    'experience',
    [] as ShortList[],
  );

  const filteredExperiences: typeof categoryExperiences =
    categoryExperiences.map(([catId, { category, experiences }]) => {
      const regionId = normalizeRegion(region) === 'sicily' ? 1 : 2;
      let filteredList: Experience[] = experiences.filter(
        (exp) => exp.regionId === regionId,
      );

      if (chosenZone.value !== 'any') {
        filteredList = filteredList.filter(
          (exp) =>
            exp.zoneId !== null && exp.zoneId.toString() === chosenZone.value,
        );
      }

      if (chosenLanguages.length > 0) {
        filteredList = filteredList.filter((exp) =>
          exp.languages.some((language) =>
            chosenLanguages.includes(language.locale),
          ),
        );
      }

      if (chosenPrice.minPrice !== 0 || chosenPrice.maxPrice !== 3000) {
        const isPriceInRange = (price: string) => {
          const numericPrice = parseFloat(price);
          return (
            numericPrice >= chosenPrice.minPrice &&
            numericPrice <= chosenPrice.maxPrice
          );
        };

        const priceFilteredList = filteredList.filter((exp) => {
          const allPrices: {
            minParticipants: number;
            maxParticipants: number;
            price: string;
          }[] = [];

          exp.optionMaps.forEach((optionMap) => {
            if (optionMap.prices) {
              allPrices.push(...optionMap.prices);
            }
          });

          return allPrices.some((priceObj) => isPriceInRange(priceObj.price));
        });

        filteredList = priceFilteredList;
      }

      return [catId, { category, experiences: filteredList }];
    });

  const totalExperiences = Object.entries(filteredExperiences).reduce(
    (acc, [, item]) => acc + item[1].experiences.length,
    0,
  );

  // useEffect(() => {
  //   setChosenTypes(experiences?.map((item) => item.id) as string[]);
  // }, [experiences]);

  useEffect(() => {
    const handleScroll = () => {
      const typeElement = document.querySelector('#type');

      if (!typeElement) return;

      const typeRect = typeElement.getBoundingClientRect();

      if (typeRect.top > 144) {
        setIsSticky(false);
      }
    };

    var root = document.getElementsByTagName('html')[0];
    document.body.classList.remove('no-scroll');
    root.classList.remove('no-scroll');

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const contentElement = document.querySelector('#sticky');

      if (!contentElement) return;

      const contentRect = contentElement.getBoundingClientRect();

      if (contentRect.top <= 0) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky]);

  return (
    <>
      <div
        id="sticky"
        className={`hidden desktop:flex flex-col sticky desktop:static -top-[60px] -mx-10 bg-white z-[999] ${
          isSticky && 'shadow-[0_5px_8px_-6px_grey] desktop:shadow-none'
        }`}>
        {totalExperiences !== 0 && (
          <div className="mb-12 mx-10">
            <span className="text-gray-700 font-text">
              {totalExperiences} {dict.experiencesFound}
            </span>
          </div>
        )}

        <div>
          <div
            className="hidden desktop:block absolute top-[35%] left-0 -rotate-90 -translate-x-[41%] translate-y-[420%]"
            onClick={() => setOpenFilter(true)}>
            <AnimatedLink className="w-fit" href="#">
              {dict.filterSearch}
            </AnimatedLink>
          </div>

          <div className="desktop:hidden flex flex-col mb-10 mx-10 gap-4">
            <div className="flex items-center gap-8 px-[15px] desktop:px-10">
              <Link href={'/sicily/experiences'}>
                <IoIosArrowBack className="h-6 w-6 text-gray-500" />
              </Link>
              <h3 className="text-2xl font-noe">{categoryTitle}</h3>
            </div>
            <div className="flex w-full gap-8">
              <Button
                variant={'outline'}
                className="h-[50px]"
                onClick={() => setOpenFilter(true)}>
                {dict.filterButton}
              </Button>
              <Button className="h-[50px]" onClick={() => setOpenFilter(true)}>
                {dict.orderButton}
              </Button>
            </div>
          </div>

          {openFilter && (
            <CategoryFilter
              category={categoryTitle}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              typesIds={chosenTypes}
              // allTypesIds={experiences?.map((item) => item.id) as string[]}
              allTypesIds={[]}
              setChosenTypes={setChosenTypes}
              chosenLanguages={chosenLanguages}
              setChosenLanguages={setChosenLanguages}
              chosenZone={chosenZone}
              setChosenZone={setChosenZone}
              chosenPrice={chosenPrice}
              setChosenPrice={setChosenPrice}
            />
          )}
        </div>
      </div>

      {totalExperiences !== 0 ? (
        filteredExperiences?.map(([catId, { category, experiences }]) => {
          const translation = category.categoryTranslation.find(
            (translation) => translation.locale === lang,
          )!;

          if (experiences.length !== 0) {
            return (
              <ExperienceType
                key={catId}
                title={translation.title}
                description={translation.description ?? ''}
                experiences={experiences}
                favourites={favourites}
                setFavourites={setFavourites}
              />
            );
          }
        })
      ) : (
        <div className="desktop:h-[500px] desktop:mt-8">
          <p>{dict.noResultsFound}</p>
        </div>
      )}
    </>
  );
};
